import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Loader2, User } from 'lucide-react';
import { COUNCIL_LEVELS } from '../../mock';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { SectionHeader } from './AdminShell';
import { MemberFormDialog, LEVEL_LABELS } from './MemberFormDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MembersSection = ({ token, onUnauthorized }) => {
  const [level, setLevel] = useState('state');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(undefined);

  const fetchMembers = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/members`, { params: { level } })
      .then((res) => setMembers(res.data || []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [level]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const deleteMember = async (m) => {
    if (!window.confirm(`Delete "${m.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/members/${m.id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchMembers();
    } catch (e) { if (e?.response?.status === 401) onUnauthorized(); }
  };

  const onSaved = (savedLevel) => { setEditing(undefined); if (savedLevel === level) fetchMembers(); else setLevel(savedLevel); };

  return (
    <>
      <SectionHeader title="Members" subtitle="Governing council and general members shown on the website.">
        <Button className="jbss-admin-add" onClick={() => setEditing(null)} data-testid="admin-add-member"><Plus size={16} /> Add Member</Button>
      </SectionHeader>
      <div className="jbss-adm-card">
        <div className="jbss-adm-card-head">
          <div>
            <Label className="jbss-admin-label">Level</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="jbss-admin-level" data-testid="admin-level-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNCIL_LEVELS.map((lv) => <SelectItem key={lv} value={lv} data-testid={`admin-level-option-${lv}`}>{LEVEL_LABELS[lv]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <span className="jbss-adm-pill" data-testid="admin-members-count">{members.length} members</span>
        </div>
        {loading ? (
          <p className="jbss-adm-empty"><Loader2 className="jbss-spin" size={18} /> Loading...</p>
        ) : members.length === 0 ? (
          <p className="jbss-adm-empty" data-testid="admin-empty">No members yet. Click "Add Member".</p>
        ) : (
          <div className="jbss-admin-table" data-testid="admin-member-list">
            {members.map((m) => (
              <div className="jbss-admin-trow" key={m.id} data-testid={`admin-member-${m.id}`}>
                <span className="jbss-app-photo">{m.photo ? <img src={`${API}/photos/${m.photo}`} alt="" /> : <User size={18} />}</span>
                <div className="jbss-admin-tinfo jbss-app-grow">
                  <span className="jbss-admin-tname">{m.name}</span>
                  <span className="jbss-admin-tmeta">{level === 'general' ? (m.location || m.contact_number || '') : (m.designation || '')}</span>
                </div>
                <div className="jbss-admin-trow-actions">
                  <button className="jbss-icon-btn" onClick={() => setEditing(m)} data-testid={`admin-edit-${m.id}`} aria-label="Edit"><Pencil size={16} /></button>
                  <button className="jbss-icon-btn jbss-icon-danger" onClick={() => deleteMember(m)} data-testid={`admin-delete-${m.id}`} aria-label="Delete"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {editing !== undefined && (
        <MemberFormDialog member={editing} defaultLevel={level} token={token} onUnauthorized={onUnauthorized}
          onClose={() => setEditing(undefined)} onSaved={onSaved} />
      )}
    </>
  );
};
